import { GoTrueClient } from "@supabase/auth-js";
import { syncCacheOwner } from "./apiCache.js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * The Supabase Auth client — exactly what `createClient(url, key).auth` builds
 * (supabase-js's `SupabaseAuthClient` is a bare subclass of this one), minus
 * the database, storage, realtime and functions clients the web never uses:
 * all data flows through our API. Built with supabase-js's defaults — above
 * all its `sb-<project ref>-auth-token` storage key — so sessions stored by
 * the earlier full client stay signed in.
 */
function createAuthClient(projectUrl: string, key: string): GoTrueClient {
  const base = new URL(projectUrl.endsWith("/") ? projectUrl : `${projectUrl}/`);
  return new GoTrueClient({
    url: new URL("auth/v1", base).href,
    headers: { Authorization: `Bearer ${key}`, apikey: key },
    storageKey: `sb-${base.hostname.split(".")[0]}-auth-token`,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: "implicit"
  });
}

/**
 * Browser Supabase client, or null when auth isn't configured (e.g. tests or
 * an offline build). Used only for login/signup/session — all data still flows
 * through our API, which verifies the resulting access token.
 */
export const supabase: { auth: GoTrueClient } | null =
  url && anonKey ? { auth: createAuthClient(url, anonKey) } : null;

export const isAuthEnabled = Boolean(supabase);

// Cached access token so apiClient can attach it synchronously. Kept fresh by
// the auth state listener below (covers login, logout, and token refresh).
let accessToken: string | null = null;

if (supabase) {
  void supabase.auth.getSession().then(({ data }) => {
    accessToken = data.session?.access_token ?? null;
    syncCacheOwner(data.session?.user?.id ?? null);
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    accessToken = session?.access_token ?? null;
    // The API cache may hold another account's private drafts — clear it
    // whenever the acting identity changes. Keying on the identity (not the
    // event name) both survives sessions that end without a SIGNED_OUT event
    // and keeps the cache warm across routine SIGNED_IN re-confirmations
    // (supabase emits one on every tab refocus).
    syncCacheOwner(session?.user?.id ?? null);
  });
}

export async function getAccessToken(): Promise<string | null> {
  if (accessToken || !supabase) return accessToken;
  const { data } = await supabase.auth.getSession();
  accessToken = data.session?.access_token ?? null;
  return accessToken;
}
